import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  Mail,
  HeartPulse,
  ArrowRight,
  Sparkles,
  FileText,
  Trash2,
} from 'lucide-react';
import WaitlistModal from './BetaTesterModal'; // adjust path if needed
import { logEvent } from '../utils/analytics';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);

  const handleJoinWaitlistClick = () => {
    logEvent('Conversion', 'Open_Waitlist_Modal', 'Footer_CTA');
    setIsWaitlistModalOpen(true);
  };

  const cardClass =
    'border border-[var(--color-brand-border)] bg-[linear-gradient(180deg,var(--color-brand-card),var(--color-brand-ai-bg))] shadow-[0_16px_40px_rgba(15,23,42,0.08)]';

  const softChip =
    'border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)] text-[var(--color-brand-text-secondary)]';

  return (
    <footer className="relative mt-24 overflow-hidden border-t border-[var(--color-brand-border)] bg-[var(--color-brand-background)]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.08),transparent_28%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_20%,rgba(16,185,129,0.07),transparent_24%)]" />
        <div className="absolute left-[-6%] top-10 h-64 w-64 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="absolute right-[-8%] bottom-0 h-72 w-72 rounded-full bg-emerald-400/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 pb-8 pt-16">
        {/* Top CTA strip */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="mb-10 rounded-[2rem] border border-[var(--color-brand-border)] bg-[linear-gradient(135deg,var(--color-brand-card),var(--color-brand-ai-bg))] p-6 shadow-[0_18px_50px_rgba(15,23,42,0.08)] md:p-8"
        >
          <div className="flex flex-col items-start justify-between gap-5 lg:flex-row lg:items-center">
            <div className="max-w-2xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-primary)]/20 bg-[var(--color-brand-primary)]/10 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-[var(--color-brand-primary)]">
                <Sparkles size={13} />
                Early access waitlist
              </div>

              <h3 className="text-2xl font-black text-[var(--color-brand-text)] md:text-3xl">
                Join the waitlist for smarter daily weight management
              </h3>
              <p className="mt-3 text-sm leading-7 text-[var(--color-brand-text-secondary)] md:text-base">
                Get early access to LesslyKilo and be among the first users shaping a smarter,
                simpler health journey.
              </p>

              <p className="mt-3 text-sm font-bold text-emerald-400">
                First 100 users to join the waitlist get lifetime free premium access.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleJoinWaitlistClick}
                className="inline-flex items-center gap-2 rounded-full bg-[linear-gradient(90deg,var(--color-brand-primary),#34d399)] px-5 py-3 text-sm font-black text-white shadow-[0_12px_28px_rgba(34,211,238,0.22)] transition-all hover:scale-[1.02] hover:brightness-110"
              >
                <Sparkles size={16} />
                Join the waitlist
              </button>

              <a
                href="#pricing"
                className="inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] px-5 py-3 text-sm font-black text-[var(--color-brand-text)] transition-all hover:border-[var(--color-brand-primary)]/30 hover:text-[var(--color-brand-primary)]"
              >
                View plans
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </motion.div>

        {/* Main footer content */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Brand block */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className="lg:col-span-5"
          >
            <div className={`relative h-full overflow-hidden rounded-[2rem] p-8 ${cardClass}`}>
              <div className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full bg-[var(--color-brand-primary)]/10 blur-3xl" />
              <div className="pointer-events-none absolute -left-10 bottom-0 h-28 w-28 rounded-full bg-emerald-400/10 blur-3xl" />

              <div className="relative z-10 flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-[var(--color-brand-primary)]/20 bg-[var(--color-brand-primary)]/10 p-2">
                  <img
                    src="/logo-original.png"
                    alt="LesslyKilo logo"
                    className="h-full w-full object-contain"
                  />
                </div>

                <div>
                  <div className="text-xl font-black tracking-tight text-[var(--color-brand-text)] md:text-2xl">
                    LesslyKilo
                  </div>
                  <div className="text-xs font-black uppercase tracking-[0.22em] text-[var(--color-brand-primary)]">
                    AI Weight Management
                  </div>
                </div>
              </div>

              <p className="relative z-10 mt-6 max-w-xl text-sm leading-8 text-[var(--color-brand-text-secondary)] md:text-base">
                LesslyKilo helps users turn everyday tracking into smarter health decisions.
                From meals and hydration to workouts, weight trends, and AI coaching, the app is
                designed to make consistency more practical and sustainable.
              </p>

              <div className="relative z-10 mt-6 flex flex-wrap gap-3">
                <div className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-cyan-300">
                  Daily tracking
                </div>
                <div className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-emerald-300">
                  AI guidance
                </div>
                <div className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] ${softChip}`}>
                  Sustainable habits
                </div>
              </div>
            </div>
          </motion.div>

          {/* Links */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.05 }}
            className="lg:col-span-7"
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Platform */}
              <div className={`rounded-[2rem] p-8 ${cardClass}`}>
                <h4 className="mb-5 text-sm font-black uppercase tracking-[0.22em] text-[var(--color-brand-text-secondary)]">
                  Platform
                </h4>

                <ul className="space-y-4">
                  <li>
                    <a
                      href="#features"
                      className="text-sm font-medium text-[var(--color-brand-text-secondary)] transition-colors hover:text-[var(--color-brand-primary)]"
                    >
                      Features & AI Insights
                    </a>
                  </li>
                  <li>
                    <a
                      href="#pricing"
                      className="text-sm font-medium text-[var(--color-brand-text-secondary)] transition-colors hover:text-[var(--color-brand-primary)]"
                    >
                      Pricing & Pro
                    </a>
                  </li>
                  <li>
                    <a
                      href="#about"
                      className="text-sm font-medium text-[var(--color-brand-text-secondary)] transition-colors hover:text-[var(--color-brand-primary)]"
                    >
                      Why LesslyKilo
                    </a>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={handleJoinWaitlistClick}
                      className="inline-flex items-center gap-2 rounded-full bg-[linear-gradient(90deg,var(--color-brand-primary),#34d399)] px-5 py-3 text-sm font-black text-white shadow-[0_12px_28px_rgba(34,211,238,0.22)] transition-all hover:scale-[1.02] hover:brightness-110"
                    >
                      <Sparkles size={16} />
                      Join the waitlist
                    </button>
                  </li>
                </ul>
              </div>

              {/* Legal + Support */}
              <div className={`rounded-[2rem] p-8 ${cardClass}`}>
                <h4 className="mb-5 text-sm font-black uppercase tracking-[0.22em] text-[var(--color-brand-text-secondary)]">
                  Support & Legal
                </h4>

                <ul className="space-y-4">
                  <li>
                    <a
                      href="/privacy"
                      className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-brand-text-secondary)] transition-colors hover:text-[var(--color-brand-primary)]"
                    >
                      <FileText size={15} />
                      Privacy Policy
                    </a>
                  </li>
                  <li>
                    <a
                      href="/terms"
                      className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-brand-text-secondary)] transition-colors hover:text-[var(--color-brand-primary)]"
                    >
                      <ShieldCheck size={15} />
                      Terms of Service
                    </a>
                  </li>
                  <li>
                    <a
                      href="/data-deletion"
                      className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-brand-text-secondary)] transition-colors hover:text-[var(--color-brand-danger)]"
                    >
                      <Trash2 size={15} />
                      Account & Data Deletion
                    </a>
                  </li>
                  <li className="pt-2">
                    <a
                      href="mailto:caselabs91@gmail.com"
                      className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-sm font-bold text-[var(--color-brand-text)] transition-all hover:border-emerald-300/30 hover:bg-emerald-400/14"
                    >
                      <Mail size={15} className="text-emerald-300" />
                      caselabs91@gmail.com
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom strip */}
        <div className="mt-10 border-t border-[var(--color-brand-border)] pt-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex max-w-4xl items-start gap-4">
              <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--color-brand-primary)]/20 bg-[var(--color-brand-primary)]/10">
                <ShieldCheck size={18} className="text-[var(--color-brand-primary)]" />
              </div>

              <p className="text-xs leading-7 text-[var(--color-brand-text-secondary)] md:text-sm">
                <strong className="text-[var(--color-brand-text)]">Medical Disclaimer:</strong>{' '}
                LesslyKilo provides nutritional, fitness, and health-tracking support based on user input.
                It is designed to support — not replace — the relationship between a user and their qualified
                healthcare provider. Always consult a health professional before starting a new diet, exercise,
                or wellness program.
              </p>
            </div>

            <div className="flex flex-col items-start gap-2 text-xs font-bold text-[var(--color-brand-text-secondary)] lg:items-end">
              <div className="flex items-center gap-2">
                Made with <HeartPulse size={14} className="text-[var(--color-brand-danger)]" />
                by <span className="text-[var(--color-brand-text)]">Viomath Tech Proprietary Limited</span>
              </div>
              <div>&copy; {currentYear} LesslyKilo. All rights reserved.</div>
            </div>
          </div>
        </div>
      </div>

      <WaitlistModal
        isOpen={isWaitlistModalOpen}
        onClose={() => setIsWaitlistModalOpen(false)}
      />
    </footer>
  );
}